/**
 * Salle vidéo native — route interne (même origine que le reste de l'app),
 * montée uniquement depuis `useJoinVisioRoom` sur Capacitor (iOS/Android).
 * Remplace le `Browser.open()` externe : la salle reste "dans" l'app, sans
 * chrome de navigateur visible. Web (PWA) n'utilise PAS cette route — il
 * garde le handoff SSO vers `apps/tutor` (tutor.jeraisonne.fr), inchangé.
 *
 * Pas de handoff/jeton ici : la session Supabase de l'app est DÉJÀ live,
 * `get_or_create_visio_room` est appelée directement (même RPC que
 * `visio-handoff-create` côté serveur — seule barrière de sécurité réelle :
 * identité + réservation confirmée + fenêtre de rejoignabilité).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { RoomPage, type VisioRole } from '@jeraisonne/visio';
import { supabase, supabaseUrl } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { translateRoomError } from '@/lib/visio/room-errors';
import { deriveVisioRole } from '@/lib/visio/derive-role';
import { buildVisioInviteUrl } from '@/lib/visio/tutor-url';

// `get_or_create_visio_room` est postérieure aux types générés (comme
// `visio_room_messages`, cf. CLAUDE.md) — client générique pour cet appel.
const db = supabase as unknown as SupabaseClient;

interface RoomRow {
  room_id: string;
}

type Screen =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; roomId: string; me: { userId: string; role: VisioRole; displayName: string } };

export function RendezVousRoomPage() {
  const { reservationId } = useParams({ strict: false }) as { reservationId: string };
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>({ kind: 'loading' });
  // Idempotence : StrictMode (dev) double-invoque l'effet au montage.
  const startedRef = useRef(false);

  useEffect(() => {
    if (authLoading || !user) return;
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const { data, error } = await db.rpc('get_or_create_visio_room', {
        p_reservation_id: reservationId,
      });
      if (error || !(data as RoomRow[] | null)?.[0]) {
        setScreen({ kind: 'error', message: translateRoomError(error?.message) });
        return;
      }
      setScreen({
        kind: 'ready',
        roomId: (data as RoomRow[])[0]!.room_id,
        me: {
          userId: user.id,
          role: deriveVisioRole(profile?.role),
          displayName: profile?.display_name ?? '',
        },
      });
    })();
  }, [authLoading, user, profile, reservationId]);

  function exitToRendezVous() {
    void navigate({ to: '/rendez-vous/$reservationId', params: { reservationId } });
  }

  if (screen.kind === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-4 py-6 sm:py-8">
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (screen.kind === 'error') {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-6 text-center sm:py-8">
        <AlertTriangle className="text-accent-500 h-8 w-8" />
        <p className="text-ink-900 text-lg font-semibold">Impossible de rejoindre la salle</p>
        <p className="text-ink-500 max-w-sm text-sm">{screen.message}</p>
        <Button onClick={exitToRendezVous} className="min-h-[2.75rem] w-full max-w-xs">
          Retour au rendez-vous
        </Button>
      </div>
    );
  }

  return (
    <RoomPage
      roomId={screen.roomId}
      me={screen.me}
      db={db}
      supabaseUrl={supabaseUrl}
      onExit={exitToRendezVous}
      exitLabel="Retour au rendez-vous"
      buildInviteUrl={buildVisioInviteUrl}
    />
  );
}
